import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';

interface DeliveryAssignment {
  id: string;
  hubId: string;
  orderId: string;
  orderSourceType: string;
  hubOrderNumber?: number;
  deliveryPartnerId?: string;
  deliveryPartnerName?: string;
  deliveryPartnerPhone?: string;
  deliveryPartnerPhotoUrl?: string | null;
  state: string;
  goodsSubtotal?: number;
  deliveryFeeAmount?: number;
  networkFeeAmount?: number;
  finalCashTotal?: number;
  collectFromCustomerAmount?: number;
  customerChangeForAmount?: number;
  cashOnDelivery?: boolean;
  routeDistanceKm?: number;
  estimatedDistanceKm?: number;
  feeLedgerStatus?: string;
  acceptedAt?: string;
  pickedUpAt?: string;
  deliveredAt?: string;
  createdOn?: string;
  // Mission 065-F/G: courier-reported issues + delivery ETA.
  issues?: { issueType?: string; note?: string; reportedAt?: string }[];
  latestIssueType?: string;
  latestIssueAt?: string;
  estimatedReadyAt?: string;
  estimatedDeliveryAt?: string;
}

/**
 * TinyAll Delivery Network — admin view of delivery assignments (any state) with simple filters.
 * Supports force-cancel for not-yet-delivered assignments. All endpoints 404 when the backend
 * feature flag (TINYALL_DELIVERY_NETWORK_ENABLED) is off.
 */
@Component({
  selector: 'app-delivery-assignments',
  templateUrl: './delivery-assignments.component.html',
  styleUrls: ['./delivery-assignments.component.scss'],
})
export class DeliveryAssignmentsComponent implements OnInit {
  private readonly apiUrl = environment.apiUrl;
  private readonly baseUrl = `${this.apiUrl}/admin-center/delivery-network/v1`;

  data: DeliveryAssignment[] = [];
  isLoading = false;
  featureDisabled = false;

  readonly stateOptions = [
    '',
    'OFFERED',
    'ACCEPTED',
    'PICKED_UP',
    'DELIVERED',
    'CANCELLED',
    'REASSIGNED',
    'FAILED',
  ];
  stateFilter = '';
  hubFilter = '';
  /** Mission 061-F: client-side refinement to surface assignments with an unsettled network fee. */
  pendingFeeOnly = false;

  private readonly cancellableStates = ['OFFERED', 'ACCEPTED'];

  constructor(
    private httpService: HttpService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const params: string[] = [];
    if (this.stateFilter) {
      params.push(`state=${encodeURIComponent(this.stateFilter)}`);
    }
    if (this.hubFilter?.trim()) {
      params.push(`hubId=${encodeURIComponent(this.hubFilter.trim())}`);
    }
    const query = params.length ? `?${params.join('&')}` : '';
    try {
      const response = await this.httpService
        .doGet(`${this.baseUrl}/assignments${query}`)
        .toPromise();
      this.data = Array.isArray(response) ? response : [];
      this.featureDisabled = false;
    } catch (error: any) {
      this.data = [];
      if (error?.status === 404) {
        this.featureDisabled = true;
      } else {
        this.toastError('Could not load delivery assignments');
      }
    } finally {
      this.isLoading = false;
    }
  }

  /** Prominent, zero-padded operational order number (e.g. 5 -> "#0005"), or "—" when unknown. */
  formatOrderNumber(value?: number | null): string {
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) {
      return '—';
    }
    return `#${String(Math.trunc(Number(value))).padStart(4, '0')}`;
  }

  /** Delivery distance for display: prefer the calculated route distance, else the straight-line estimate. */
  formatDistanceKm(item: DeliveryAssignment): string {
    const km = item.routeDistanceKm ?? item.estimatedDistanceKm;
    return km == null ? '—' : `${km.toFixed(1)} km`;
  }

  /** Mission 065-F: compact label for the latest courier-reported issue, or "—". */
  latestIssueLabel(item: DeliveryAssignment): string {
    const type = item.latestIssueType ?? item.issues?.[item.issues.length - 1]?.issueType;
    if (!type) {
      return '—';
    }
    const count = item.issues?.length ?? 1;
    const label = type
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return count > 1 ? `${label} (+${count - 1})` : label;
  }

  /** Rows after the client-side pending-fee refinement (state/hub filters are server-side). */
  get displayedData(): DeliveryAssignment[] {
    if (!this.pendingFeeOnly) {
      return this.data;
    }
    return this.data.filter((d) => d.feeLedgerStatus === 'PENDING');
  }

  feeLedgerSeverity(status?: string): string {
    switch (status) {
      case 'PENDING':
        return 'ledger-PENDING';
      case 'CHARGED':
        return 'ledger-CHARGED';
      case 'WAIVED':
        return 'ledger-WAIVED';
      case 'REVIEW':
        return 'ledger-REVIEW';
      default:
        return 'ledger-NONE';
    }
  }

  canCancel(item: DeliveryAssignment): boolean {
    return this.cancellableStates.indexOf(item.state) !== -1;
  }

  confirmCancel(item: DeliveryAssignment): void {
    this.confirmationService.confirm({
      message: `Force-cancel assignment for order #${item.hubOrderNumber ?? item.orderId}?`,
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.cancel(item),
    });
  }

  private async cancel(item: DeliveryAssignment): Promise<void> {
    try {
      await this.httpService
        .doPost(
          `${this.baseUrl}/assignments/${encodeURIComponent(item.id)}/cancel?reason=${encodeURIComponent(
            'Admin cancelled'
          )}`,
          {}
        )
        .toPromise();
      this.toastSuccess('Assignment cancelled');
      this.loadData();
    } catch (error: any) {
      this.toastError(error?.error?.message || 'Could not cancel assignment');
    }
  }

  stateSeverity(state: string): string {
    switch (state) {
      case 'DELIVERED':
        return 'state-DELIVERED';
      case 'OFFERED':
        return 'state-OFFERED';
      case 'ACCEPTED':
        return 'state-ACCEPTED';
      case 'PICKED_UP':
        return 'state-PICKED_UP';
      case 'CANCELLED':
      case 'FAILED':
        return 'state-CANCELLED';
      default:
        return 'state-OTHER';
    }
  }

  private toastSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Successful', detail, life: 3000 });
  }

  private toastError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail, life: 4000 });
  }
}
